/**
 * caseColor
 *
 * Deterministic mapping from a case id to a brand-friendly accent colour.
 * Used by the cross-case calendar and dashboard so renters managing
 * multiple tenancies can visually differentiate events at a glance.
 *
 * The palette is hand-picked to sit on the cream paper background, stay
 * legible at thin chip widths, and remain distinct from the global
 * teal / navy primary colours that mean "Compass" everywhere else.
 *
 * Each entry exposes:
 *   stripe : the strong border-left colour on chips and cards
 *   bg     : the soft tint behind the chip text
 *   ink    : ink/text colour for labels inside the soft tint
 */
export const CASE_PALETTE = [
  { stripe: "#1B6C62", bg: "#E6F5F2", ink: "#0D2B52" }, // teal
  { stripe: "#8A6D3B", bg: "#FAF6E8", ink: "#0D2B52" }, // ochre
  { stripe: "#9C3D54", bg: "#FBEAEF", ink: "#0D2B52" }, // rose
  { stripe: "#3D6E9C", bg: "#E8F0FA", ink: "#0D2B52" }, // slate blue
  { stripe: "#6E4C9C", bg: "#EFE8FA", ink: "#0D2B52" }, // muted plum
  { stripe: "#3D8A6B", bg: "#E8F5EF", ink: "#0D2B52" }, // sage
  { stripe: "#B8722E", bg: "#FBEFDD", ink: "#0D2B52" }, // amber
  { stripe: "#4A6E6A", bg: "#E8F0EE", ink: "#0D2B52" }, // moss
  { stripe: "#7A4E8A", bg: "#F1E8F4", ink: "#0D2B52" }, // mulberry
  { stripe: "#2E6E8A", bg: "#E0EEF4", ink: "#0D2B52" }, // ocean
  { stripe: "#A85A3D", bg: "#F8E4DC", ink: "#0D2B52" }, // terracotta
  { stripe: "#5E7A2E", bg: "#EEF2DD", ink: "#0D2B52" }, // olive
];

/** Stable, non-cryptographic 32-bit hash for short strings (FNV-1a),
 *  followed by a Murmur-style finalizer so that hash % palette.length
 *  distributes evenly even when case_ids share a UUID prefix family. */
function hashString(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  // fmix32 finalizer (from MurmurHash3)
  h ^= h >>> 16;
  h = Math.imul(h, 2246822507) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 3266489909) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

/** Return the palette entry assigned to a given case id. */
export function colorForCase(caseId) {
  if (!caseId) return CASE_PALETTE[0];
  return CASE_PALETTE[hashString(String(caseId)) % CASE_PALETTE.length];
}

/** Build a Map of case_id -> palette entry for an array of cases. */
export function buildCaseColorMap(cases = []) {
  const map = new Map();
  for (const c of cases) {
    if (!c || !c.id) continue;
    map.set(c.id, colorForCase(c.id));
  }
  return map;
}
